import React from 'react';
import { Offer, Package } from '@spilca/order_ts/v1/order/model_offer_pb';
import { useI18n } from '@monorepo/i18n';
import { FLEX_ALIGN, FLEX_JUSTIFY, FlexBox } from '@monorepo/ui-kit';
import { DateField, Form, InputField, TextAreaField, CurrencySelect } from '@monorepo/forms';
import {
  offerToFormValues,
  TSaveOffer,
  DeliveryConditionSelect,
  PaymentConditionSelect,
  OfferDocuments,
  FinancialSupportTypeSelect,
  DeliveryPriceAppliedSelect
} from '@monorepo/offers';
import PackagingSelect from '@monorepo/offers/components/PackagingSelect';
import PaymentTypeSelect from '@monorepo/dictionary/components/PaymentTypeSelect';
import AmountField from '@monorepo/forms/components/MaskedNumberField';
import TransportTypeSelect from '@monorepo/offers/components/DeliveryTypeSelect';
import useGetPriceLists from '@monorepo/pricing/hooks/useGetPriceLists';
import useGetPackageIDs from '@monorepo/requests/hooks/useGetPackageIDs';
import PackagingQuantity from '@monorepo/offers/components/OfferForm/components/PackageQuantity';
import styles from './index.module.css';
import TaxSelect from '../TaxSelect';

type OfferFormProps = {
  offer: Offer.AsObject;
  isBackOffice: boolean;
  isCommodities: boolean;
};

const OfferForm: Component<OfferFormProps> = ({
  offer,
  isBackOffice,
  isCommodities
}) => {
  const { t } = useI18n();
  const defaultValues = offerToFormValues(offer);

  const { data } = useGetPriceLists({
    pageSize: 20,
    defaultFilter: { product_id: offer.product?.id ? [offer.product.id] : [] }
  });

  const items = data?.pages[0].itemsList ?? [];

  const { data: packageData, convertDataForGrid } = useGetPackageIDs({
    pageSize: 50,
    productId: offer.product?.id
  });

  const [packaging, setPackaging] = React.useState<Package.AsObject[]>(() =>
    (offer.packageIdsList ?? []).map((p) => ({
      packageId: p.packageId,
      amount: p.amount
    }))
  );

  return (
    <Form<TSaveOffer> formSettings={{ defaultValues }} className={styles.form}>
      <div className={styles.container}>
        <div>
          <InputField<TSaveOffer>
            className={styles.inputGroup}
            id="brandName"
            name="brandName"
            label={t('offer.form.brand.label')}
            placeholder={t('offer.form.brand.placeholder')}
            disabled
          />
          <InputField<TSaveOffer>
            className={styles.inputGroup}
            id="productName"
            name="productName"
            label={t('offer.form.product.label')}
            placeholder={t('offer.form.product.placeholder')}
            disabled
          />
          <FlexBox className={`${styles.inputGroup}`}>
            <InputField<TSaveOffer>
              className={styles.inputInline}
              id="quantity"
              name="quantity"
              label={t('offer.form.quantity.label')}
              placeholder={t('offer.form.quantity.placeholder')}
              disabled
            />
            <InputField<TSaveOffer>
              className={styles.inputInline}
              id="quantityTypeName"
              name="quantityTypeName"
              label={t('offer.form.quantityType.label')}
              placeholder={t('offer.form.quantityType.placeholder')}
              disabled
            />
          </FlexBox>
          <FlexBox className={`${styles.inputGroup}`} align={FLEX_ALIGN.end}>
            <InputField<TSaveOffer>
              className={styles.inputInline}
              id="price"
              name="price"
              label={t('offer.form.price.label')}
              placeholder={t('offer.form.price.placeholder')}
              disabled
            />
            <InputField<TSaveOffer>
              className={styles.inputInline}
              id="vatRate"
              name="vatRate"
              label={t('offer.form.vatRate.label')}
              placeholder={t('offer.form.vatRate.placeholder')}
              disabled
            />
          </FlexBox>
          {isCommodities && (
            <PackagingSelect<TSaveOffer>
              className={styles.inputGroup}
              id={`${offer.id}.packaging`}
              name={`${offer.id}.packaging`}
              label={t('offer.form.packaging.label')}
              placeholder={t('offer.form.packaging.placeholder')}
              disabled
            />
          )}
        </div>
        <div>
          <PaymentConditionSelect<TSaveOffer>
            className={styles.inputGroup}
            id="paymentCondition"
            name="paymentCondition"
            label={t('offer.form.paymentCondition.label')}
            placeholder={t('offer.form.paymentCondition.placeholder')}
            disabled
          />
          {isCommodities && (
            <PaymentTypeSelect<TSaveOffer>
              className={styles.inputGroup}
              id={`${offer.id}.paymentType`}
              name={`${offer.id}.paymentType`}
              label={t('offer.form.paymentType.label')}
              placeholder={t('offer.form.paymentType.placeholder')}
              disabled
            />
          )}
          {isCommodities ? (
            <AmountField<TSaveOffer>
              className={styles.inputGroup}
              id={`${offer.id}.paymentTerm`}
              name={`${offer.id}.paymentTerm`}
              label={t('offer.form.paymentTerm.label')}
              placeholder={t('offer.form.paymentTerm.placeholder')}
              disabled
            />
          ) : (
            <DateField<TSaveOffer>
              className={styles.inputGroup}
              id={`${offer.id}.paymentDate`}
              name={`${offer.id}.paymentDate`}
              label={t('offer.form.paymentDate.label')}
              placeholder={t('offer.form.paymentDate.placeholder')}
              disabled
            />
          )}
          <DateField<TSaveOffer>
            className={styles.inputGroup}
            id="expiredDate"
            name="expiredDate"
            label={t('offer.form.expireDate.label')}
            placeholder={t('offer.form.expiredDate.placeholder')}
            disabled
          />
          <FlexBox
            className={`${styles.inputGroup}`}
            justify={FLEX_JUSTIFY.between}
            style={{ marginTop: '32px' }}
          >
            <TaxSelect<TSaveOffer>
              className={`${styles.inputInline} ${styles.currency}`}
              id="vatApplied"
              name="vatApplied"
              label={t('offer.form.vatApplied.label')}
              placeholder={t('offer.form.vatApplied.placeholder')}
              disabled
            />
            <DeliveryPriceAppliedSelect<TSaveOffer>
              className={`${styles.inputInline} ${styles.currency}`}
              id="deliveryPriceApplied"
              name="deliveryPriceApplied"
              label={t('offer.form.deliveryPriceApplied.label')}
              placeholder={t('offer.form.deliveryPriceApplied.placeholder')}
              disabled
            />
            <CurrencySelect
              className={styles.inputInline}
              inputClassName={styles.currency}
              id="currency"
              name="currency"
              label={t('offer.form.currency.label')}
              placeholder={t('offer.form.currency.placeholder')}
              disabled
            />
          </FlexBox>
        </div>
        <div>
          <FinancialSupportTypeSelect<TSaveOffer>
            id="collateral"
            name="collateral"
            label={t('offer.form.collateral.label')}
            defaultValue="not selected"
            disabled
          />
          <DateField<TSaveOffer>
            className={styles.inputGroup}
            id="deliveryDate"
            name="deliveryDate"
            label={t(
              isCommodities
                ? 'offer.form.deliveryDateFrom.label'
                : 'offer.form.deliveryDate.label'
            )}
            placeholder={t('offer.form.deliveryDate.placeholder')}
            disabled
          />
          {isCommodities && (
            <DateField<TSaveOffer>
              className={styles.inputGroup}
              id={`${offer.id}.deliveryDeadline`}
              name={`${offer.id}.deliveryDeadline`}
              label={t('offer.form.deliveryDeadline.label')}
              placeholder={t('offer.form.deliveryDeadline.placeholder')}
              inputValue={{ defaultValue: defaultValues.deliveryDeadline }}
              disabled
            />
          )}
          <TextAreaField<TSaveOffer>
            className={styles.inputGroup}
            inputClassName={styles.deliveryPlace}
            id="deliveryPlace"
            name="deliveryPlace"
            label={t('offer.form.deliveryPlace.label')}
            placeholder={t('offer.form.deliveryPlace.placeholder')}
            disabled
          />
          <DeliveryConditionSelect<TSaveOffer>
            className={styles.inputGroup}
            id="deliveryCondition"
            name="deliveryCondition"
            label={t('offer.form.deliveryCondition.label')}
            placeholder={t('offer.form.deliveryCondition.placeholder')}
            disabled
          />
          {isBackOffice && (
            <InputField<TSaveOffer>
              className={styles.inputInline}
              id="supplierId"
              name="supplierId"
              label={t('offer.form.supplierId.label')}
              placeholder={t('offer.form.supplierId.placeholder')}
              disabled
            />
          )}
          {isCommodities && (
            <TransportTypeSelect<TSaveOffer>
              className={styles.inputGroup}
              id={`${offer.id}.transportType`}
              name={`${offer.id}.transportType`}
              label={t('offer.form.transportType.label')}
              placeholder={t('offer.form.transportType.placeholder')}
              inputValue={{ value: defaultValues.transportType }}
              disabled
            />
          )}
        </div>
        <div>
          <OfferDocuments offerId={offer.id} isAddDisabled isDeleteDisabled />
        </div>
      </div>
      <PackagingQuantity
        readOnly
        offer={offer}
        items={items}
        productPackage={convertDataForGrid(packageData)}
        packaging={packaging}
        setPackaging={setPackaging}
      />
      <div>
        <TextAreaField<TSaveOffer>
          className={`${styles.inputGroup} ${styles.commentGroup}`}
          inputClassName={styles.comment}
          id="description"
          name="description"
          label={t('offer.form.comment.label')}
          placeholder={t('offer.form.comment.placeholder')}
          disabled
        />
      </div>
    </Form>
  );
};

export default OfferForm;
